import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useCurrency } from '../../contexts/CurrencyContext';
import PWAInstallPrompt from '../PWAInstallPrompt';
import OfflineIndicator from '../OfflineIndicator';
import { hasPermission } from '../../utils/permissions';
import FiscalYearSelector from '../FiscalYearSelector';

// Company Branding from Environment Variables
const COMPANY_NAME = process.env.REACT_APP_COMPANY_NAME || 'CRM';
const COMPANY_LOGO_URL = process.env.REACT_APP_COMPANY_LOGO_URL || '';
const PRIMARY_COLOR = process.env.REACT_APP_PRIMARY_COLOR || '#1e40af';
const SECONDARY_COLOR = process.env.REACT_APP_SECONDARY_COLOR || '#16a34a';

const MobileLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { companySettings } = useCurrency();
  const [showProfileMenu, setShowProfileMenu] = useState(false);

  const isActive = (path) => location.pathname === path;

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: '🏠', module: 'dashboard' },
    { path: '/daily-tasks', label: 'Daily Tasks', icon: '✅', module: 'daily_tasks' },
    { path: '/attendance', label: 'Attendance', icon: '🕒', module: 'attendance' },
    { path: '/attendance-report', label: 'Attendance Report', icon: '🗓️', module: 'attendance_report' },
    { path: '/activities', label: 'Activities', icon: '📋', module: 'activities' },
    { path: '/customers', label: 'Customers', icon: '👥', module: 'customers' },
    { path: '/products', label: 'Products', icon: '📦', module: 'products' },
    { path: '/stock-report', label: 'Stock Report', icon: '📊', module: 'stock_report' },
    { path: '/leads', label: 'Leads', icon: '🎯', module: 'leads' },
    { path: '/sales-invoices', label: 'Sales Invoices', icon: '💰', module: 'sales_invoices' },
    { path: '/delivery-notes', label: 'Delivery Notes', icon: '🚚', module: 'delivery_notes' },
    { path: '/service-requests', label: 'Service Requests', icon: '🛎️', module: 'service_requests' },
    { path: '/payments', label: 'Customer Payments', icon: '💳', module: 'payments' },
    { path: '/supplier-payments', label: 'Supplier Payments', icon: '🏦', module: 'supplier_payments' },
    { path: '/sales-reports', label: 'Sales Reports', icon: '📈', module: 'sales_reports' },
    { path: '/suppliers', label: 'Suppliers', icon: '🏭', module: 'suppliers' },
    { path: '/purchase-orders', label: 'Purchase Orders', icon: '📑', module: 'purchase_orders' },
    { path: '/purchase-entries', label: 'Purchase Entries', icon: '📦', module: 'purchase_entries' },
    // VAT Return — accountants/data-entry users (admins always allowed).
    { path: '/vat-return', label: 'VAT Return', icon: '📊', module: 'vat_return' },
    // Other Purchases — assets / office expenses / utilities (feeds VAT line 13).
    { path: '/other-purchases', label: 'Other Purchases', icon: '📂', module: 'other_purchases' },
  ];

  const adminMenuItems = [
    { path: '/users', label: 'Users', icon: '👤', module: 'users' },
    { path: '/teams', label: 'Teams', icon: '👥', module: 'teams' },
    { path: '/location-tracking', label: 'Locations', icon: '📍', module: 'location_tracking' },
    // GPS Map view — Live + Trail with stop-detection. Same `location_tracking`
    // module permission as the list view; backend mirrors this on every call.
    { path: '/location-map', label: 'GPS Map', icon: '🗺️', module: 'location_tracking' },
    { path: '/master-data', label: 'Master Data', icon: '⚙️', module: 'master_data' },
    { path: '/reports', label: 'Reports', icon: '📊', module: 'reports' },
    { path: '/admin/bulk-delete', label: 'Bulk Delete', icon: '🗑️', module: 'bulk_delete' },
    // admin / super_admin only: DB backup workflow.
    { path: '/admin/backups', label: 'Backups', icon: '💾', adminOnly: true },
    // admin / super_admin only: read-only DB integrity diagnostics (orphan scan).
    { path: '/admin/diagnostics', label: 'Diagnostics', icon: '🔧', adminOnly: true },
    // admin / super_admin only: fiscal year configuration (Phase 1 of FY work).
    { path: '/admin/fiscal-years', label: 'Fiscal Years', icon: '📅', adminOnly: true },
    // admin / super_admin only: post-close edit history (Phase 3 of FY work).
    { path: '/admin/fy-audit-log', label: 'FY Audit Log', icon: '📋', adminOnly: true },
    { path: '/admin/override-log', label: 'Override Log', icon: '🔐', adminOnly: true },
    { path: '/settings', label: 'Settings', icon: '🔧', module: 'company_settings' },
    { path: '/approval-settings', label: 'Approval Settings', icon: '✅', module: 'approval_settings' },
  ];

  const filteredNavItems = navItems.filter((item) => {
    if (item.requiresEdit) return hasPermission(user, item.module, 'edit');
    return hasPermission(user, item.module, 'view');
  });
  const filteredAdminItems = adminMenuItems.filter((item) => {
    if (item.adminOnly) return ['admin', 'super_admin'].includes(user?.role);
    return hasPermission(user, item.module, 'view');
  });

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      logout();
    }
    setShowProfileMenu(false);
  };

  return (
    <div className="flex flex-col h-screen bg-gradient-to-br from-blue-50 via-green-50 to-blue-50">
      <OfflineIndicator />
      <PWAInstallPrompt />
      
      {/* Top Header - Mobile */}
      <header className="lg:hidden bg-white shadow-sm border-b sticky top-0 z-40">
        <div className="flex justify-between items-center px-3 py-2.5">
          {/* Logo and Back Button - Left Side */}
          <div className="flex items-center space-x-3 flex-shrink-0">
            {/* Back to Dashboard Button - Only show when NOT on dashboard */}
            {location.pathname !== '/dashboard' && (
              <button
                onClick={() => navigate('/dashboard')}
                className="p-2 rounded-lg hover:bg-blue-50 active:bg-blue-100 transition-colors"
                aria-label="Back to Dashboard"
              >
                <svg className="w-7 h-7 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}
            
            {/* Logo */}
            <button 
              onClick={() => navigate('/dashboard')}
              className="flex items-center"
            >
              {(companySettings?.logo_url || COMPANY_LOGO_URL) ? (
                <img 
                  src={companySettings?.logo_url || COMPANY_LOGO_URL} 
                  alt={COMPANY_NAME} 
                  className="h-10 w-auto object-contain"
                />
              ) : (
                <div 
                  className="p-2 rounded-lg"
                  style={{
                    background: `linear-gradient(to right, ${PRIMARY_COLOR}, ${SECONDARY_COLOR})`
                  }}
                >
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
              )}
            </button>
          </div>
          
          {/* User Name and Profile - Right Side */}
          <div className="flex items-center space-x-3">
            <FiscalYearSelector variant="mobile" />
            <h2 className="text-base sm:text-lg md:text-xl font-bold text-gray-800" data-testid="user-name-header">{user?.name}</h2>
            <button
              onClick={() => setShowProfileMenu(!showProfileMenu)}
              className="p-1.5 rounded-lg hover:bg-gray-100 flex items-center gap-1"
              data-testid="profile-menu-btn"
            >
              <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-green-600 rounded-full flex items-center justify-center text-white text-sm font-bold">
                {user?.name?.charAt(0)?.toUpperCase() || 'U'}
              </div>
              <svg className="w-3 h-3 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            
            {/* Profile Dropdown */}
            {showProfileMenu && (
              <>
                {/* Backdrop for mobile */}
                <div 
                  className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
                  onClick={() => setShowProfileMenu(false)}
                />
                
                {/* Dropdown Menu */}
                <div className="absolute right-0 mt-2 w-full sm:w-80 lg:w-64 bg-white rounded-lg shadow-xl border z-50 max-h-[85vh] overflow-y-auto" data-testid="profile-dropdown">
                <div className="p-3 border-b bg-gradient-to-r from-blue-50 to-green-50">
                  <p className="font-semibold text-gray-900 text-sm">{user?.name}</p>
                  <p className="text-xs text-gray-600 mt-0.5">{user?.email}</p>
                  <span className="inline-block mt-2 px-2 py-1 bg-blue-600 text-white text-xs rounded-full font-medium">
                    {user?.role?.toUpperCase()}
                  </span>
                </div>
                
                {/* Main Navigation */}
                <div className="py-1 border-b">
                  <p className="px-3 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wide">Main Menu</p>
                  {filteredNavItems.map((item) => (
                    <button
                      key={item.path}
                      onClick={() => {
                        navigate(item.path);
                        setShowProfileMenu(false);
                      }}
                      className={`w-full text-left px-3 py-2.5 flex items-center gap-2 text-sm transition-colors ${
                        isActive(item.path) 
                          ? 'bg-blue-50 text-blue-700 font-semibold' 
                          : 'hover:bg-gray-50 text-gray-700'
                      }`}
                    >
                      <span className="text-lg">{item.icon}</span>
                      <span>{item.label}</span>
                    </button>
                  ))}
                </div>
                
                {/* Admin Menu Items */}
                {filteredAdminItems.length > 0 && (
                  <div className="py-1 border-b">
                    <p className="px-3 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wide">Admin Menu</p>
                    {filteredAdminItems.map((item) => (
                      <button
                        key={item.path}
                        onClick={() => {
                          navigate(item.path);
                          setShowProfileMenu(false);
                        }}
                        className="w-full text-left px-3 py-2.5 hover:bg-blue-50 flex items-center gap-2 text-sm transition-colors"
                      >
                        <span className="text-lg">{item.icon}</span>
                        <span>{item.label}</span>
                      </button>
                    ))}
                  </div>
                )}
                
                {/* Logout for ALL users */}
                <div className="py-1">
                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-3 py-3 hover:bg-red-50 flex items-center gap-2 text-red-600 text-sm font-semibold transition-colors"
                  >
                    <span className="text-lg">🚪</span>
                    <span>Logout</span>
                  </button>
                </div>
              </div>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Desktop Sidebar + shared Main content area. Children are mounted
          exactly ONCE so every page renders once across mobile + desktop —
          this prevents duplicate data-testids and double effects. */}
      <div className="flex flex-1 overflow-hidden">
        <aside className="hidden lg:flex w-64 bg-white shadow-lg border-r flex-col">
          <div className="p-6 border-b">
            <div className="flex items-center justify-center">
              {companySettings?.logo_url ? (
                <img src={companySettings.logo_url} alt="Logo" className="h-16 w-auto" />
              ) : (
                <div className="bg-gradient-to-r from-blue-600 to-green-600 p-2 rounded-lg">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
              )}
            </div>
          </div>

          <nav className="flex-1 overflow-y-auto py-4">
            {/* User Welcome - Near Dashboard */}
            <div className="mx-4 mb-3 px-3 py-2 bg-gradient-to-r from-blue-50 to-green-50 rounded-lg">
              <p className="text-xs text-gray-600">Welcome,</p>
              <h3 className="text-base font-bold text-gray-800">{user?.name}</h3>
              {/* Global Fiscal Year selector on desktop. Visible on every
                  page so users can switch active FY at any time. */}
              <div className="mt-2 -mx-1" data-testid="desktop-fy-selector-wrapper">
                <FiscalYearSelector variant="desktop" />
              </div>
            </div>
            {filteredNavItems.map((item) => (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={`w-full text-left px-6 py-3 flex items-center space-x-3 transition-colors ${
                  isActive(item.path)
                    ? 'bg-gradient-to-r from-blue-50 to-green-50 border-l-4 border-blue-600 text-blue-700 font-semibold'
                    : 'hover:bg-gray-50 text-gray-700'
                }`}
                data-testid={`nav-${item.path.replace('/', '')}`}
              >
                <span className="text-xl">{item.icon}</span>
                <span>{item.label}</span>
              </button>
            ))}

            {filteredAdminItems.length > 0 && (
              <>
                <div className="px-6 py-2 mt-4">
                  <p className="text-xs font-semibold text-gray-400 uppercase">Admin</p>
                </div>
                {filteredAdminItems.map((item) => (
                  <button
                    key={item.path}
                    onClick={() => navigate(item.path)}
                    className={`w-full text-left px-6 py-3 flex items-center space-x-3 transition-colors ${
                      isActive(item.path)
                        ? 'bg-gradient-to-r from-blue-50 to-green-50 border-l-4 border-blue-600 text-blue-700 font-semibold'
                        : 'hover:bg-gray-50 text-gray-600'
                    }`}
                    data-testid={`nav-${item.path.replace('/', '')}`}
                  >
                    <span className="text-xl">{item.icon}</span>
                    <span>{item.label}</span>
                  </button>
                ))}
              </>
            )}
          </nav>

          <div className="p-4 border-t">
            <button
              onClick={handleLogout}
              className="w-full px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg flex items-center justify-center space-x-2 transition-colors"
              data-testid="desktop-logout-btn"
            >
              <span>🚪</span>
              <span>Logout</span>
            </button>
          </div>
        </aside>

        <main className="flex-1 overflow-y-auto">
          {children}
        </main>
      </div>
    </div>
  );
};

export default MobileLayout;
